import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { css } from '@emotion/css';
import appLabels from '../../utils/appLabels';
import { ModalContext, ModalContextType } from '../../common/Modal/Modal';
import { invoke } from 'lodash';

const generalStyle = css`
  display: flex;
  flex-direction: column;

  @media (max-width: 641px) {
    overflow-y: auto;
  }
`;

const labelStyle = css`
  margin-left: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.3125rem;
`;
const radioStyle = css`
  width: 1.5rem;
  height: 1.5rem;
  accent-color: #7b1a27;
  vertical-align: middle;
  cursor: pointer;
  flex-shrink: 0;
`;

const textAreaStyle = css`
  width: 100%;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #495057;
  font-size: 0.875rem;
  line-height: 1.3125rem;
  height: 10rem;
  box-sizing: border-box;
`;

const counterStyle = css`
  text-align: end;
  font-size: 0.5rem;
  color: #535250;
  display: inline-block;
  position: absolute;
  right: 0.5rem;
  bottom: 0.5rem;
`;

const submitButtonStyle = css`
  width: 14.625rem;
  height: 3rem;
  border-radius: 6.25rem;
  border: none;
`;

const enabledButtonStyle = css`
  background: #7b1a27;
  color: #fff;
`;

const disabledButtonStyle = css`
  background: #f1f0ee;
  color: #535250;
`;
const footerSectionStyle = css`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
`;

const radioRowStyle = css`
  display: flex;
  margin-bottom: 0.5rem;
`;

const cancelationTitleStyle = css`
  font-size: 1rem;
  line-height: 1.5rem;
`;

const fieldRequiredStyle = css`
  font-size: 0.7rem;
  line-height: 1rem;
  color: #7b1a27;
  flex: 1;
  margin: 0;
`;

const counterRowStyle = css`
  display: flex;
`;

const textAreaContainerStyle = css`
  position: relative;
  width: 100%;
`;

const CancelItemSubscription = ({ cancellationReasons, submitHandler }) => {
  const currentModalContext = useContext(ModalContext) as ModalContextType;
  //  sort cancellationReasons by reasonSequence
  cancellationReasons.sort((a, b) => a.reasonSequence - b.reasonSequence);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty, isValid },
    setValue,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      ...cancellationReasons.reduce(
        (r, reasons) => ({
          ...reasons,
          [`cancelOption_${r.reasonSequence}`]: null,
        }),
        {},
      ),
      otherDescription: '',
    },
  });
  const [count, setCount] = useState(0);
  const showTextArea = new RegExp(`^${watch('cancelOption')}$`, 'i').test(appLabels.CancelProductReasons.other);
  const maxLength = 255;
  const disableButton = !isDirty || !isValid;

  useEffect(() => {
    invoke(currentModalContext, 'setScrollable', false);
  }, []);

  const optionList = cancellationReasons.map((option, idx) => (
    <div className={radioRowStyle}>
      <input
        tabIndex={0}
        id={`radio-${idx}`}
        aria-roledescription="radio button"
        aria-label={option.reason}
        className={radioStyle}
        type="radio"
        value={option.reason}
        checked={new RegExp(`^${option.reason}$`, 'i').test(watch('cancelOption'))}
        {...register(`cancelOption`)}
      />
      <label htmlFor={`radio-${idx}`} className={labelStyle}>
        {option.reason}
      </label>
    </div>
  ));

  return (
    <form
      className={`item-list-container ${generalStyle}`}
      onSubmit={handleSubmit(submitHandler)}
      data-testid="cancel-item-subscription-form"
    >
      <p className={cancelationTitleStyle}>{appLabels.CancelProductReasons.title}</p>
      <div className="item-list-container" aria-live="polite">
        <div role="radiogroup">{optionList}</div>
        {errors.cancelOption && <p className={fieldRequiredStyle}>You must select an option</p>}
        {showTextArea && (
          <div>
            <div className={textAreaContainerStyle}>
              <textarea
                maxLength={maxLength}
                className={textAreaStyle}
                placeholder={appLabels.CancelProductReasons.placeHolder}
                {...register('otherDescription', {
                  maxLength: maxLength,
                  validate: (value) => {
                    setCount(value.length);
                    return showTextArea ? value != '' && new RegExp(/^(?!\s*$).+/).test(value) : true;
                  },
                })}
              />
              <span className={counterStyle}>
                {count}/{maxLength}
              </span>
            </div>

            <div className={counterRowStyle}>
              <p className={fieldRequiredStyle}>{errors.otherDescription ? 'This field is required' : ''}</p>
            </div>
          </div>
        )}
      </div>

      <div className={footerSectionStyle}>
        <input
          aria-label="Confirm"
          aria-roledescription="button"
          disabled={disableButton}
          className={`${submitButtonStyle} ${!disableButton ? enabledButtonStyle : disabledButtonStyle}`}
          type="submit"
          value="Confirm"
        />
      </div>
    </form>
  );
};

export default CancelItemSubscription;
